import React from 'react';
import { Box, Typography, CircularProgress, Stack } from '@mui/material';
import { toast } from 'react-hot-toast';
import Cookies from 'js-cookie';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';
import EditorialButton from '../components/editorial/EditorialButton';

import TierSelector from '../components/membership/TierSelector';
import InclusionsSection from '../components/membership/InclusionsSection';
import WhereItGoesSection from '../components/membership/WhereItGoesSection';
import ImpactStatsSection from '../components/membership/ImpactStatsSection';
import HardshipNote from '../components/membership/HardshipNote';
import HouseholdForm from '../components/membership/HouseholdForm';
import SeatForm from '../components/membership/SeatForm';
import MembershipCheckout from '../components/membership/MembershipCheckout';
import { RETURN_PATHS } from '../service/checkoutReturn';
import StickyTallyBar from '../components/membership/StickyTallyBar';
import InviteRedemptionModal from '../components/modal/InviteRedemptionModal';
import RolloutBanner from '../components/membership/RolloutBanner';

import {
  getMembershipTiers,
  getSeatLockInfo,
  enrollInMembership,
  enrollGuestSeats,
} from '../api/membership/membershipApi';
import {
  getStepsForTier,
  buildEnrollPayload,
  buildGuestSeatsPayload,
  getNextIncompleteStep,
} from '../service/enrollmentFlow';
import { getSeatPricing } from '../config/membershipDefaults';
import { getTierBadge, getEnrollmentSummary } from '../service/membershipPricing';
import { syncIncludedSeatPeople } from '../service/seatPricing';
import { emptyHousehold, knownFilledHouseholdFields, mergeSignedInHousehold } from '../service/householdValidation';
import { updateUserPhone } from '../api/users/saveUsers';
import { authedFetch, waitForAuthUser } from '../api/authedFetch';


const emptySeats = () => ({
  includedSeatPeople: [],
  additionalSeatPeople: [],
});

const emptyGuestContact = () => ({ name: '', email: '' });

// Membership 5787 — one continuous flow, section order locked by the
// client's wireframe: Hero -> What Membership Includes (hardship line at
// its very bottom) -> Where Your Membership Goes -> Choose Your
// Commitment (tiers) -> High Holiday Seats -> Your Household -> Review &
// Confirm. "Where Your Membership Goes" isn't named in the locked
// section-order list (which reads like it's enumerating the interactive
// SIGNUP FLOW steps, not every marketing section) — kept here, right
// after Includes, since nothing asked for it to be cut; flag if the
// client meant otherwise.
//
// The High Holiday Seats section always renders, before a tier is ever
// picked — a visitor who never selects one sees it in guest mode (0
// included seats, non-member rate, and the "become a member instead"
// comparison card as the loudest element). The Household and Review &
// Confirm sections only appear once a tier is selected AND the seat step
// is confirmed, so for a guest the Seats section really is the last thing
// on the page, per the wireframe's intent.
export default function Membership2026() {
  const [tiers, setTiers] = React.useState([]);
  const [tiersLoading, setTiersLoading] = React.useState(true);
  const [tiersError, setTiersError] = React.useState(null);

  const [seatLockInfo, setSeatLockInfo] = React.useState(null);

  // One-time/annual is the default (client decision, Aug 2026 — the
  // wireframe originally defaulted to monthly).
  const [billingCycle, setBillingCycle] = React.useState('annual');
  const [selectedTier, setSelectedTier] = React.useState(null);

  const [seatsConfirmed, setSeatsConfirmed] = React.useState(false);
  const [householdConfirmed, setHouseholdConfirmed] = React.useState(false);

  const [household, setHousehold] = React.useState(emptyHousehold());
  const [seats, setSeats] = React.useState(emptySeats());
  const [guestContact, setGuestContact] = React.useState(emptyGuestContact());

  const [enrolling, setEnrolling] = React.useState(false);
  const [enrollError, setEnrollError] = React.useState(null);
  const [clientSecret, setClientSecret] = React.useState(null);
  const [enrollDone, setEnrollDone] = React.useState(false);

  // Guest-only High Holiday seat checkout — POST /api/membership/enroll-guest-seats,
  // separate from the member enrollment state above since a guest never
  // touches the household/review steps.
  const [guestSeatsEnrolling, setGuestSeatsEnrolling] = React.useState(false);
  const [guestSeatsError, setGuestSeatsError] = React.useState(null);
  const [guestSeatsClientSecret, setGuestSeatsClientSecret] = React.useState(null);

  const [activateModalOpen, setActivateModalOpen] = React.useState(false);
  const [signedInEmail, setSignedInEmail] = React.useState(null);
  const [hiddenHouseholdFields, setHiddenHouseholdFields] = React.useState([]);

  const includesSectionRef = React.useRef(null);
  const tiersSectionRef = React.useRef(null);
  const seatsSectionRef = React.useRef(null);
  const householdSectionRef = React.useRef(null);
  const reviewSectionRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getMembershipTiers();
        if (!cancelled) setTiers(data);
      } catch (err) {
        if (!cancelled) setTiersError(err.message);
      } finally {
        if (!cancelled) setTiersLoading(false);
      }
    })();
    (async () => {
      try {
        const info = await getSeatLockInfo();
        if (!cancelled) setSeatLockInfo(info);
      } catch (err) {
        // Soft-fail: if seat-lock-info can't be reached, default to
        // showing the seat form rather than hard-blocking the whole page.
        console.error('Error loading seat-lock info:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const user = await waitForAuthUser();
      if (cancelled || !user) return;
      const email = user.email || null;
      if (email) setSignedInEmail(email);

      let profile = null;
      try {
        const res = await authedFetch('/me/household');
        if (res.ok) {
          const json = await res.json();
          profile = json.household || null;
        }
      } catch (err) {
        console.error('Error loading household for enrollment:', err);
      }
      if (cancelled) return;

      const merged = mergeSignedInHousehold({
        profile,
        email,
        displayName: user.displayName,
      });
      setHousehold(merged);
      setHiddenHouseholdFields(knownFilledHouseholdFields(merged));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollTo = (ref) => {
    setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };

  // "Just here for High Holiday guest seats? Start here" — both the hero
  // link and a direct /membership2026#guest visit land on the benefits
  // section (never the seats section), already in guest-viewing mode:
  // guest mode IS the default (no tier selected), so there's nothing
  // extra to "arm" beyond making sure the landing spot is right.
  React.useEffect(() => {
    if (window.location.hash === '#guest') {
      scrollTo(includesSectionRef);
    }
  }, []);

  const handleGuestLinkClick = (e) => {
    e.preventDefault();
    window.history.replaceState(null, '', '#guest');
    scrollTo(includesSectionRef);
  };

  const handleChooseMembershipClick = () => {
    scrollTo(tiersSectionRef);
  };

  const handleBecomeMemberClick = () => {
    scrollTo(tiersSectionRef);
  };

  // Clicking an already-selected tier passes null here (see TierCard) —
  // that's a deselect, not a re-selection. Handled as one function since
  // both cases need the same included-seat resync.
  const handleSelectTier = (tier) => {
    setSelectedTier(tier);
    // Included-seat rows follow the tier's allowance (Family 2, Singles 1,
    // First-Year Couples 0, none once deselected) — sync rather than
    // reset, so switching tiers mid-flow doesn't discard names already
    // typed into rows that still exist.
    setSeats((prev) => ({
      ...prev,
      includedSeatPeople: syncIncludedSeatPeople(prev.includedSeatPeople, tier?.includedSeats ?? 0),
    }));
    if (tier) {
      scrollTo(seatsSectionRef);
    } else {
      // Deselecting takes the visitor back to guest mode — the household
      // and review steps are gated on selectedTier and disappear on their
      // own, but their "confirmed" flags need clearing too, or re-picking
      // a tier later would skip straight past steps that were never
      // actually completed for THIS tier.
      setSeatsConfirmed(false);
      setHouseholdConfirmed(false);
    }
  };

  const handleSeatsContinue = () => {
    setSeatsConfirmed(true);
    scrollTo(householdSectionRef);
  };

  // Guest-only seat purchase — no tier, no household, just contact info +
  // the seat names/counts already collected in SeatForm. Returns a Stripe
  // clientSecret for a one-time PaymentIntent at the non-member rate;
  // SeatForm renders the same MembershipCheckout used for member payments
  // once this resolves. On successful payment the backend posts a
  // QuickBooks SalesReceipt and sends the seats-reserved email itself —
  // nothing further to do here.
  const handleGuestRequestSubmitted = async ({ seats: requestedSeats, guestContact: contact }) => {
    setGuestSeatsEnrolling(true);
    setGuestSeatsError(null);
    try {
      const payload = buildGuestSeatsPayload({ guestContact: contact, seats: requestedSeats });
      const result = await enrollGuestSeats(payload);
      if (!result?.clientSecret) {
        throw new Error('Could not start guest seat checkout');
      }
      setGuestSeatsClientSecret(result.clientSecret);
    } catch (err) {
      setGuestSeatsError(err.message);
      toast.error(err.message);
    } finally {
      setGuestSeatsEnrolling(false);
    }
  };

  const handleHouseholdContinue = () => {
    setHouseholdConfirmed(true);
    scrollTo(reviewSectionRef);
  };

  const submitEnrollment = async () => {
    setEnrolling(true);
    setEnrollError(null);
    try {
      const payload = buildEnrollPayload({ tier: selectedTier, billingCycle, household, seats });
      const result = await enrollInMembership(payload);
      if (result?.clientSecret) {
        setClientSecret(result.clientSecret);
      } else {
        // Free tier (or a backend that confirms enrollment outright).
        setEnrollDone(true);
        toast.success('Welcome to KBT! Your membership is set up.');
      }
    } catch (err) {
      setEnrollError(err.message);
      toast.error(err.message);
    } finally {
      setEnrolling(false);
    }
  };

  // Mirrors the existing handleAccountCreated pattern (see Header.js on
  // feature/kbt-174-invite-flow) rather than inventing a new one — sets
  // the same cookies the rest of the site reads for a signed-in member,
  // then sends them to their account instead of restarting this signup.
  const handleAccountActivated = async (res) => {
    if (res?.uid) {
      Cookies.set('uid', res.uid);
      Cookies.set('email', res.email);
      if (res.customerStripeID) {
        Cookies.set('customerStripeID', res.customerStripeID);
      }
      if (res.phoneNumber && res.phoneVerified) {
        try {
          await updateUserPhone(res.uid, res.phoneNumber, true);
          Cookies.set('phoneVerified', 'true');
        } catch (err) {
          console.error('Failed to update phone after invite activation:', err);
        }
      }
    }
    setActivateModalOpen(false);
    window.location.href = '/my-account';
  };

  const seatPricing = getSeatPricing(seatLockInfo);
  const recommendedTier = tiers.find((t) => getTierBadge(t) === 'MOST POPULAR') || tiers[0] || null;
  const enrollmentSummary = getEnrollmentSummary({
    tier: selectedTier,
    billingCycle,
    seats,
    seatPricing,
  });
  const showsPayment =
    selectedTier &&
    getStepsForTier(selectedTier, { extraSeatCost: enrollmentSummary?.extraSeatCost }).includes('payment');

  // Lets the tally bar's CONTINUE run SeatForm's own guest submit, so both
  // buttons share one validation path.
  const seatFormRef = React.useRef(null);

  const stepSectionRefs = {
    tier: tiersSectionRef,
    seats: seatsSectionRef,
    household: householdSectionRef,
    review: reviewSectionRef,
  };

  const handleTallyBarContinue = () => {
    const guestSeatRows = seats.additionalSeatPeople?.length ?? 0;

    // No tier but seats entered means a guest, not someone still deciding —
    // so CONTINUE runs the guest request instead of sending them back to the
    // membership list they deliberately skipped, which was a dead end.
    // Falling through to getNextIncompleteStep would return 'tier' here.
    //
    // The seats-and-no-tier test is what separates the two readings of "no
    // tier selected": with no seats either, the visitor genuinely has not
    // chosen yet, the bar still reads "Pick a membership above", and the
    // old scroll-to-tiers behaviour is right.
    if (!selectedTier && guestSeatRows > 0) {
      scrollTo(seatsSectionRef);
      // Already past the request and looking at Stripe — just bring the
      // payment form back into view rather than creating a second intent.
      if (!guestSeatsClientSecret) {
        seatFormRef.current?.submitGuestRequest();
      }
      return;
    }

    const nextStep = getNextIncompleteStep({ selectedTier, seatsConfirmed, householdConfirmed });
    scrollTo(stepSectionRefs[nextStep]);
  };

  return (
    <EditorialPageShell
      title="Membership 5787"
      description="Become a member of KBT — pay once, or in twelve monthly payments."
    >
      <PageHero
        kicker="Membership 5787  ·  KBT"
        meta="An annual partnership"
        title="Join the kehilla"
        titleAccent="behind the kehilla."
        subtitle="Membership is an annual partnership — pay once, or in twelve monthly payments. Choose the tier that fits your household, then tell us who's in it."
        image="/assets/images/kbt/shul-gathering.jpg"
        height="80vh"
        primaryCta={{ label: 'Choose your membership ↓', onClick: handleChooseMembershipClick }}
      >
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          <Box
            component="a"
            href="#activate"
            onClick={(e) => {
              e.preventDefault();
              setActivateModalOpen(true);
            }}
            sx={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'rgba(255,255,255,0.85)',
              textDecoration: 'underline',
              textDecorationColor: 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
            }}
          >
            Already a member? Activate your account
          </Box>
          <Box
            component="a"
            href="#guest"
            onClick={handleGuestLinkClick}
            sx={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'rgba(255,255,255,0.85)',
              textDecoration: 'underline',
              textDecorationColor: 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
            }}
          >
            Just here for High Holiday guest seats? Start here ↓
          </Box>
        </Stack>
      </PageHero>

      <RolloutBanner rollout={seatLockInfo?.rollout} signedIn={Boolean(signedInEmail)} />

      <Box ref={includesSectionRef} />
      <EditorialSection
        kicker="What's included"
        title="What membership"
        titleAccent="includes."
        intro="Guests are always welcome at KBT — membership adds to that, it never limits it."
      >
        <InclusionsSection />
        <HardshipNote />
      </EditorialSection>

      <EditorialSection
        kicker="Where it goes"
        title="Where your membership"
        titleAccent="goes."
        background="var(--paper-warm, #faf8f4)"
      >
        <WhereItGoesSection />
      </EditorialSection>

      <EditorialSection
        kicker="Since 2022"
        title="KBT by the"
        titleAccent="numbers."
        intro="A growing center for Torah, Tefillah, Halacha, and community — this is what your membership actually funds."
        align="center"
      >
        <ImpactStatsSection />
      </EditorialSection>

      <Box ref={tiersSectionRef} />
      <EditorialSection
        kicker="Step 1 of 4  ·  Membership"
        title="Choose your"
        titleAccent="membership."
        intro="Prefer to settle it in one payment? Toggle to one-time / annual. Monthly is a committed, 12-payment pledge — not a subscription you can cancel anytime."
        align="center"
        watermark="כ"
      >
        <TierSelector
          tiers={tiers}
          loading={tiersLoading}
          error={tiersError}
          billingCycle={billingCycle}
          onBillingCycleChange={setBillingCycle}
          selectedTierId={selectedTier?.id}
          onSelectTier={handleSelectTier}
        />
      </EditorialSection>

      <Box ref={seatsSectionRef} />
      <EditorialSection
        kicker="Step 2 of 4  ·  Seats"
        title="High Holiday"
        titleAccent="seats."
        background="var(--paper-warm, #faf8f4)"
      >
        <SeatForm
          ref={seatFormRef}
          tier={selectedTier}
          seats={seats}
          onChange={setSeats}
          guestContact={guestContact}
          onGuestContactChange={setGuestContact}
          seatPricing={seatPricing}
          recommendedTier={recommendedTier}
          onContinue={handleSeatsContinue}
          onGuestRequestSubmitted={handleGuestRequestSubmitted}
          onBecomeMemberClick={handleBecomeMemberClick}
          guestCheckoutState={{
            enrolling: guestSeatsEnrolling,
            error: guestSeatsError,
            clientSecret: guestSeatsClientSecret,
          }}
        />
      </EditorialSection>

      {selectedTier && seatsConfirmed && (
        <EditorialSection kicker="Step 3 of 4  ·  Household" title="Tell us about your" titleAccent="household." id="household-form">
          <Box ref={householdSectionRef} />
          <HouseholdForm
            household={household}
            onChange={setHousehold}
            onContinue={handleHouseholdContinue}
            lockedEmail={signedInEmail}
            hiddenFields={hiddenHouseholdFields}
          />
        </EditorialSection>
      )}

      {selectedTier && seatsConfirmed && householdConfirmed && (
        <EditorialSection kicker="Step 4 of 4  ·  Confirm" title="Review &" titleAccent="confirm.">
          <Box ref={reviewSectionRef} maxWidth={560} mx="auto">
            {!clientSecret && !enrollDone && (
              <Stack spacing={3}>
                <Box sx={{ p: 3, border: '1px solid rgba(36,34,88,0.15)', background: 'var(--paper)' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {household.householdName || 'Household'} · {household.email}
                  </Typography>

                  {enrollmentSummary && (
                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                      <Typography variant="body2">{enrollmentSummary.membershipLine}</Typography>
                      {enrollmentSummary.seatsLine && (
                        <Typography variant="body2">{enrollmentSummary.seatsLine}</Typography>
                      )}
                    </Stack>
                  )}

                  {enrollmentSummary && (
                    <>
                      <Typography variant="h6" fontWeight={700}>
                        {enrollmentSummary.total}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {enrollmentSummary.dueTodayNote}
                      </Typography>
                    </>
                  )}
                </Box>

                <EditorialButton variant="primary" onClick={submitEnrollment} disabled={enrolling} fullWidth>
                  {showsPayment ? 'Confirm & continue to payment' : 'Confirm membership'}
                </EditorialButton>

                {enrolling && (
                  <Stack alignItems="center" py={2}>
                    <CircularProgress sx={{ color: 'var(--navy-500)' }} />
                  </Stack>
                )}
                {enrollError && <Typography color="error">{enrollError}</Typography>}
              </Stack>
            )}

            {clientSecret && showsPayment && (
              <Box sx={{ mt: 3 }}>
                <MembershipCheckout clientSecret={clientSecret} returnPath={RETURN_PATHS.membership} />
              </Box>
            )}

            {enrollDone && (
              <Typography align="center" sx={{ mt: 2 }}>
                Thank you for partnering with KBT. Our office will follow up with any next steps —
                reach out any time at office@kbtla.org.
              </Typography>
            )}
          </Box>
        </EditorialSection>
      )}

      <InviteRedemptionModal
        open={activateModalOpen}
        handleClose={() => setActivateModalOpen(false)}
        onAccountCreated={handleAccountActivated}
      />

      {!enrollDone && <Box sx={{ height: { xs: 96, sm: 72 } }} />}
      {!enrollDone && <StickyTallyBar summary={enrollmentSummary} onContinue={handleTallyBarContinue} />}
    </EditorialPageShell>
  );
}
